<template>
  <s-loader
    v-if="loading"
    class="analysis-tab__loader p-5"
    />
  <b-container
    v-else
    fluid
    >
    <b-row align-v="end" class="mb-3 analysis-tab__row">
      <b-col
        cols="12"
        sm="12"
        md="12"
        lg="6"
      >
        <b-row>
          <b-col cols="12" sm="12" md="6">
            <label>{{$t('engineering.sample-date')}}*</label>
            <s-input-date-picker
              v-model="payload.date"
              :time-zone="timeZoneUTC"
              @lazy-blur-native-proxy="onBlurDate(payload.date, 'engineering.sample-date')"
              :state="dateValidator"
            />
          </b-col>
          <b-col cols="12" sm="12" md="6">
            <label>{{$t('engineering.sample-time')}}*</label>
            <s-input-time
              v-model="payload.time"
              step="1"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col
        cols="12"
        sm="12"
        md="12"
        lg="6"
      >
        <label>{{$t(`fields.physical-chemical-analysis.sample-type`)}}*</label>
        <s-select
          v-model="payload.sampleType"
          :options="sampleTypeOptions"
          :state="$bsStateValidator($v.payload.sampleType.$error)"
          @blur.native="$v.payload.sampleType.$touch()"
          @change="updateSamplingMoment"
        />
      </b-col>
    </b-row>
    <b-row class="my-2" v-for="accordion in accordions" :key="accordion.type">
      <b-col cols="12">
        <accordion-panel
          :id="accordion.type"
          :collapsed.sync="accordion.collapsed"
        >
          <b-row class="pl-3" slot="title">
            {{$t(`engineering.test-information.${accordion.type}`)}}
          </b-row>
          <b-container fluid>
            <b-row align-v="end">
              <b-col
                v-for="(input, index) in accordion.inputs"
                :key="index"
                :cols="input.breakpoint.cols"
                :sm="input.breakpoint.sm"
                :md="input.breakpoint.md"
                :lg="input.breakpoint.lg"
                :xl="input.breakpoint.xl"
              >
                <label>
                  {{ $t(`${input.label}`, { ...input }) }}<!--
                  --><span v-show="input.isRequired">*</span>
                </label>
                <div class="input__height">
                  <component
                    v-model="payload[input.model]"
                    editable
                    step="1"
                    :is="input.tag"
                    :id="input.model"
                    :options="input.options"
                    :input-type="input.type"
                    :numeric-options="configureNumericOptions(input)"
                    :input-options="getInputOptions(input)"
                    :time-zone="input.timeZone"
                    :disabled="input.disabled"
                    @lazy-blur-native-proxy="onBlurTrigger(input)"
                  />
                  <b-form-invalid-feedback id="input-live-feedback">
                    {{ getRangeValidationMessage(input) }}
                  </b-form-invalid-feedback>
                </div>
              </b-col>
            </b-row>
          </b-container>
        </accordion-panel>
      </b-col>
    </b-row>
    <b-container fluid>
      <b-row align-h="end" class="no-internal-print">
        <b-col
          cols="12"
          sm="6"
          md="12"
          lg="3"
          xl="3"
          class="mt-3"
        >
          <b-button
            block
            variant="secondary"
            @click="confirmAction"
          >
            {{$t('actions.cancel')}}
          </b-button>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="12"
          lg="3"
          xl="3"
          class="mt-3"
        >
          <b-button
            @click="actionHandler"
            block
            :disabled="!canSave()"
            variant="primary" >
              {{$t('actions.save')}}
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>
<script>
import { PhysicalChemicalSample } from 'sigma-mappings';
import { required } from 'vuelidate/lib/validators';
import AccordionPanel from '@/components/AccordionPanel';
import sampleService from '@/domain/services/installation/engineering/physicalChemical/sample';
import { sampleType, samplingMoment } from '@/domain/enums/sample';
import tabNames from '@/domain/enums/installation/engineering/physicalChemical/physicalChemicalTabs';
import LoadingMixin from '@/services/loadingMixin';
import {
  formatter as dateTimeFormatter,
  getInitialDate,
  isLessOrEqual,
  joinISODateAndTimeString,
  normalizeToStartOfDayInUserZone,
  setToStartOfDay,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import fieldsDefinitions from '@/domain/data/installation/engineering/physicalChemical/fieldsDefinitions';

const minDate = getInitialDate();

export default {
  name: 'AnalysisTab',
  components: {
    AccordionPanel,
  },
  mixins: [
    LoadingMixin,
  ],
  data() {
    return {
      payload: new PhysicalChemicalSample(),
      accordions: [],
      timeZoneUTC: timezones.UTC,
    };
  },
  validations() {
    const minDateValidator = (value) => {
      if (value == null) return true;
      const valueNormalized = normalizeToStartOfDayInUserZone(value);
      return isLessOrEqual(minDate, valueNormalized);
    };

    return {
      payload: {
        sampleType: {
          required,
        },
        date: {
          required,
          minValue: minDateValidator,
        },
        time: {
          required,
        },
        analyzeDate: {
          minValue: minDateValidator,
        },
        samplingMoment: {
          required,
        },
      },
    };
  },
  computed: {
    dateValidator() {
      return this.payload.date && !this.$v.payload.date.minValue ? false : null;
    },
    nullCase() {
      return {
        text: this.$t('general.select'),
        value: null,
      };
    },
    fields() {
      return fieldsDefinitions.fields(this);
    },
    emptyFormValidator() {
      if (!this.payload) return null;

      const payloadWithoutSamplingMoment = Object.entries(this.payload).filter(item => item[0] !== 'samplingMoment');

      const payloadHasSomeValue = payloadWithoutSamplingMoment.some(item => item[1]);

      return payloadHasSomeValue;
    },
    sampleTypeOptions() {
      return [
        this.nullCase,
        {
          value: sampleType.NEW,
          text: this.$t('engineering.physical-chemical.new-oil-sample'),
        },
        {
          value: sampleType.USED,
          text: this.$t('engineering.physical-chemical.used-oil-sample'),
        },
        {
          value: sampleType.REGENERATED,
          text: this.$t('engineering.physical-chemical.regenerated-oil-sample'),
        },
        {
          value: sampleType.RECONDICIONED,
          text: this.$t('engineering.physical-chemical.recondicioned-oil-sample'),
        },
      ];
    },
    disableSamplingMoment() {
      return this.payload.sampleType !== 'new';
    },
  },
  mounted() {
    this.setDefaultDropdownValues();
    this.getAnalysisIfEditing();
    this.setAccordions();
    this.updateSamplingMoment();
  },
  methods: {
    setAccordions() {
      this.accordions = [
        {
          type: 'main-tests',
          collapsed: false,
          inputs: this.fields.primaryFields,
        },
        {
          type: 'complementary-tests',
          collapsed: true,
          inputs: this.fields.secondaryFields,
        },
        {
          type: 'additional-tests',
          collapsed: true,
          inputs: this.fields.extraFields,
        },
      ];
    },
    getAnalysisIfEditing() {
      const { sampleId } = this.$route.query;
      if (sampleId) {
        this.getAnalysis();
      }
    },
    onBlurTrigger(input) {
      const { model, label } = input;

      if (model !== 'analyzeDate') return;
      if (!this.$v.payload.analyzeDate.minValue) {
        this.showAlert(label);
      }
    },
    onBlurDate(field, fieldLabel) {
      if (!field) return;
      if (!this.$v.payload.date.minValue) {
        this.showAlert(fieldLabel);
      }
    },
    configureNumericOptions(input) {
      return {
        decimalPlaces: input.decimals,
        minimumValue: input.minValue,
        maximumValue: input.maxValue,
      };
    },
    getInputOptions(input) {
      const baseOptions = { invalid: this.validationProxy(input) === false };
      const maxLength = input.optionalSettings?.maxLength;
      if (maxLength != null) {
        baseOptions.maxlength = maxLength;
      }
      return baseOptions;
    },
    canSave() {
      return this.$v.payload.sampleType.required
        && this.$v.payload.date.minValue
        && this.$v.payload.time.required
        && this.$v.payload.date.required;
    },
    showAlert(field) {
      this.$pushAlert({
        code: 'MA038',
        messageParams: {
          NomeDoCampo: this.$t(field),
          FaixaInicial: dateTimeFormatter(minDate),
        },
      });
    },
    confirmAction() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => {
        this.goToGeneralTab();
      });
    },
    getAnalysis() {
      const { assetModuleId } = this.$route.params;
      const { sampleId } = this.$route.query;
      this.startLoading();
      sampleService.get(assetModuleId, sampleId)
        .then((data) => {
          this.payload = data;
          this.setDefaultDropdownValues();
          this.sliptDateTimeValuesInUTC();
        })
        .finally(() => {
          this.updateSamplingMoment();
          this.endLoading();
        });
    },
    sliptDateTimeValuesInUTC() {
      const {
        analysisDateTime,
        sampleDateTime,
      } = this.payload;
      const opts = { timeZone: this.timeZoneUTC };
      this.payload = {
        ...this.payload,
        date: setToStartOfDay(sampleDateTime, opts),
        time: dateTimeFormatter(sampleDateTime, formats.ISO_TIME, this.timeZoneUTC),
        analyzeDate: setToStartOfDay(analysisDateTime, opts),
        analyzeTime: dateTimeFormatter(analysisDateTime, formats.ISO_TIME, this.timeZoneUTC),
      };
    },
    setDefaultDropdownValues() {
      this.payload.samplingMoment = this.payload.samplingMoment || samplingMoment.AFTER_FILLING;
    },
    getRangeValidation({
      validation,
      minValue,
      maxValue,
      model,
    }) {
      const valueProxy = this.payload[model];
      if (!valueProxy || !validation) {
        return null;
      }
      return (valueProxy >= minValue) && (valueProxy <= maxValue);
    },
    getRangeValidationMessage(field) {
      const numberOptions = {
        minimumFractionDigits: field.decimals,
        maximumFractionDigits: field.decimals,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(field.minValue, numberOptions),
        maxValue: this.$n(field.maxValue, numberOptions),
      });
    },
    validationProxy(value) {
      const rangeValidation = this.getRangeValidation(value);
      return rangeValidation === true ? null : rangeValidation;
    },
    blockTabChange(block) {
      this.$emit('cant-leave-changed', block);
    },
    updateSamplingMoment() {
      const samplingMomentInput = this.getInputFieldByModel('samplingMoment');
      if (this.disableSamplingMoment) {
        this.$set(samplingMomentInput, 'disabled', true);
        this.$set(this.payload, 'samplingMoment', samplingMoment.AFTER_FILLING);
        return;
      }
      this.$set(samplingMomentInput, 'disabled', false);
    },
    getInputFieldByModel(model) {
      const allInputs = this.accordions.reduce((prev, next) => prev.concat(next.inputs), []);
      const field = allInputs.find(input => input.model === model);
      return field || {};
    },
    actionHandler() {
      this.$confirmMessage({ code: 'MC011' }).accepted(() => {
        this.onConfirmAction();
      });
    },
    onConfirmAction() {
      this.handleFieldsBeforeSave();
      if (this.$route.query.sampleId) {
        this.editAnalysis();
        return;
      }
      this.saveAnalysis();
    },
    handleFieldsBeforeSave() {
      const { payload } = this;
      if (!payload.samplingMoment) {
        payload.samplingMoment = 'after-filling';
      }
      if (payload.analyzeDate && !payload.analyzeTime) {
        payload.analyzeTime = '00:00:00';
      }
    },
    editAnalysis() {
      const { assetModuleId } = this.$route.params;
      const { sampleId } = this.$route.query;
      this.joinDateTimeValues();
      sampleService.update(assetModuleId, sampleId, this.payload)
        .then(() => {
          this.confirmationToast();
          this.goToGeneralTab();
        });
    },
    saveAnalysis() {
      const { assetModuleId } = this.$route.params;
      this.joinDateTimeValues();
      sampleService.insert(assetModuleId, this.payload)
        .then(() => {
          this.confirmationToast();
          this.goToGeneralTab();
        });
    },
    joinDateTimeValues() {
      const {
        date,
        time,
        analyzeDate,
        analyzeTime,
      } = this.payload;
      const opts = { timeZone: this.timeZoneUTC };
      this.payload = {
        ...this.payload,
        sampleDateTime: joinISODateAndTimeString(date, time, opts),
        analysisDateTime: joinISODateAndTimeString(analyzeDate, analyzeTime, opts),
      };
    },
    confirmationToast() {
      this.$emit('form-update', false);
      this.$pushAlert({ code: 'MS021' });
    },
    goToGeneralTab() {
      this.$router.push({
        name: 'monitoring.engineering.module',
        params: this.$route.params,
        query: {
          tab: tabNames.GENERAL,
        },
      });
    },
  },
  watch: {
    emptyFormValidator(newValue) {
      this.blockTabChange(newValue);
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.analysis-tab
  &__row
    padding: 16px 29px
  &__loader
    font-size: 2.5em

.input
  &__height
    min-height: 5em
    position: relative
    +media-min-lg()
      min-height: 6em
      </style>
